import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  construirCamposFormularioDomicilio,
  puedeEditarDomicilio,
  validarCamposDomicilio,
  valoresInicialesFormularioDomicilio
} from '../../helpers/domicilioPermisos'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useAuth } from '../../hooks/useAuth.js'
import { setModal } from '../../slices/modalSlice'
import { GenericModal } from '../reutilizables/modal/GenericModal'
import * as domiciliosService from '../servicios/domiciliosService'
import * as fetchLocalidadesAPI from '../servicios/fetchLocalidadesAPI'
import * as fetchProvinciasAPI from '../servicios/fetchProvinciasAPI'

const MODAL_ID = 'editar-domicilio'

const buscarLocalidades = async (provincia) => {
  try {
    const request = new Request(
      `https://apis.datos.gob.ar/georef/api/localidades?provincia=${encodeURIComponent(provincia)}&max=1000`
    )
    const resp = await fetch(request)
    const data = await resp.json()
    if (!data.errores) {
      return data.localidades.map((l) => l.nombre)
    }
  } catch {
    // Se recurre al fallback de EBM
  }

  const { data: localidades } = await fetchLocalidadesAPI.buscarLocalidadesEnEBM(provincia)
  return localidades
}

const ModalDomicilio = ({
  show,
  domicilio = null,
  domicilios = [],
  onClose,
  onDomicilioAgregado = () => {},
  onDomicilioActualizado = () => {}
}) => {
  const dispatch = useDispatch()
  const location = useLocation()

  const { values } = useSelector((state) => state.genericModal)
  const provinciasRef = useRef([])
  const { loginDataContext: { idUsuario, tipoUsuario } = {} } = useAuth()
  const ruta = location.pathname
  const esAlta = !domicilio
  const editable = esAlta || puedeEditarDomicilio(tipoUsuario, domicilio, ruta === '/Perfil')

  const inicializar = async () => {
    const { data: provincias } = await fetchProvinciasAPI.buscarProvinciasEnEBM()
    provinciasRef.current = provincias

    const localidades =
      esAlta || !domicilio.provincia ? [] : await buscarLocalidades(domicilio.provincia)

    dispatch(
      setModal({
        show: true,
        size: 'lg',
        title: esAlta ? 'Agregar Nuevo Domicilio' : `Domicilio #${domicilio.idUsuarioDomicilio}`,
        type: 'form',
        modalId: MODAL_ID,
        editMode: true,
        errores: {},
        values: esAlta
          ? valoresInicialesFormularioDomicilio
          : {
              idUsuarioDomicilio: domicilio.idUsuarioDomicilio,
              tipoDomicilio: domicilio.tipoDomicilio,
              calle: domicilio.calle,
              calleAltura: domicilio.calleAltura,
              provincia: domicilio.provincia,
              cp: domicilio.cp,
              localidad: domicilio.localidad,
              vieneDeEBM: domicilio.vieneDeEBM,
              idUsuario,
              domicilioPrincipal: domicilio.domicilioPrincipal,
              domicilioFacturacion: domicilio.domicilioFacturacion,
              anotaciones: domicilio.anotaciones,
              piso: domicilio.piso,
              departamento: domicilio.departamento
            },
        campos: construirCamposFormularioDomicilio({
          provincias,
          localidades,
          domicilio,
          tipoUsuario,
          incluirCamposExtras: esAlta || domicilio?.vieneDeEBM,
          editable
        })
      })
    )
  }

  useEffect(() => {
    if (!show) return
    inicializar()
  }, [show, domicilio, dispatch])

  const handleChange = async (e) => {
    const { name, value, checked, type } = e.target
    const valorFinal = type === 'checkbox' ? checked : value
    const camposBase = {
      provincias: provinciasRef.current,
      domicilio,
      tipoUsuario,
      incluirCamposExtras: esAlta || domicilio?.vieneDeEBM,
      editable
    }
    if (name === 'provincia') {
      dispatch(
        setModal({
          values: { ...values, provincia: value, localidad: '', ciudad: '' },
          campos: construirCamposFormularioDomicilio({
            ...camposBase
          })
        })
      )
      const localidades = await buscarLocalidades(value)
      dispatch(
        setModal({
          campos: construirCamposFormularioDomicilio({
            ...camposBase,
            localidades
          })
        })
      )
      return
    }

    if (name === 'localidad') {
      dispatch(setModal({ values: { ...values, localidad: value, ciudad: value } }))
      return
    }

    dispatch(setModal({ values: { ...values, [name]: valorFinal } }))
  }

  const agregarDomicilio = async () => {
    const { data: domicilioInsertado } = await domiciliosService.insertar({
      ...values,
      idUsuario,
      vieneDeEBM: true
    })

    const vieneDeCompras = ruta === '/Compra'
    if (vieneDeCompras) {
      await domiciliosService.guardarPreferidos(
        idUsuario,
        [domicilioInsertado.idUsuarioDomicilio],
        vieneDeCompras
      )
      onDomicilioAgregado({ ...domicilioInsertado, esPreferido: true })
      return true
    }
    onDomicilioAgregado(domicilioInsertado)
    return true
  }

  const actualizarDomicilio = async () => {
    const domicilioActualizado = {
      idUsuario: values.idUsuario,
      idUsuarioDomicilio: values.idUsuarioDomicilio,
      tipoDomicilio: values.tipoDomicilio,
      domicilioPrincipal: values.domicilioPrincipal,
      domicilioFacturacion: values.domicilioFacturacion,
      calle: values.calle,
      calleAltura: values.calleAltura,
      piso: values.piso,
      departamento: values.departamento,
      provincia: values.provincia,
      localidad: values.localidad,
      ciudad: values.localidad,
      cp: values.cp
    }

    await domiciliosService.actualizarDomicilio(domicilioActualizado)
    onDomicilioActualizado(domicilioActualizado)
    return true
  }

  const handleConfirm = async () => {
    if (!editable) return true

    const errores = validarCamposDomicilio(values, domicilios)
    if (Object.keys(errores).length > 0) {
      dispatch(setModal({ errores }))
      return false
    }
    try {
      if (esAlta) {
        return await agregarDomicilio()
      } else {
        return await actualizarDomicilio()
      }
    } catch (error) {
      const mensaje = 'Ocurrió un error al guardar el domicilio.'
      toastErrorLog(error, mensaje, 'domicilio')
      return false
    }
  }

  return (
    <GenericModal
      modalId={MODAL_ID}
      onChange={handleChange}
      onConfirm={handleConfirm}
      onClose={onClose}
    />
  )
}

ModalDomicilio.propTypes = {
  show: PropTypes.bool.isRequired,
  domicilio: PropTypes.object,
  domicilios: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onDomicilioAgregado: PropTypes.func,
  onDomicilioActualizado: PropTypes.func,
  esPerfil: PropTypes.bool
}

export default ModalDomicilio
